import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  AlertTriangle,
  Bell,
  BellOff,
  BellRing,
  CheckCheck,
  CheckCircle2,
  Eraser,
  FileCheck,
  FilePlus,
  FileText,
  FileX,
  Files,
  History,
  Inbox,
  Mail,
  MailCheck,
  MailX,
  Paperclip,
  PenLine,
  Reply,
  Send,
  ShieldAlert,
  StickyNote,
  Users,
  type LucideIcon,
} from "lucide-react"
import { useState, type ReactNode } from "react"
import { useNavigate } from "react-router"
import type {
  InquiryTimelineEvent,
  InquiryTimelineTone,
  SupplierInquiryDetail,
  TimelineEmail,
} from "../../../api/supplierInquiriesApi"
import { useAcceptInquiryDeclaration } from "../../../api/supplierInquiriesApi"
import { formatDate } from "../../../utils/date"
import { NoteText } from "../../shared/notes/NoteText"
import { SectionLabel } from "../../shared/SectionLabel"
import {
  Timeline,
  type TimelineItem,
  type TimelineTone,
} from "../../shared/Timeline"
import { InquiryEmailSheet } from "./InquiryEmailSheet"
import { SupplierMessageBlock } from "./SupplierMessageBlock"
import { timelineDetailText, timelineNoteQuote } from "./timelineDetail"

// Known event types get a dedicated icon; unknown types fall back to History.
const EVENT_ICON: Record<string, LucideIcon> = {
  drafted: Inbox,
  sent: Send,
  reminded: Bell,
  reply_received: MailCheck,
  declaration_linked: FileText,
  additional_declaration: FilePlus,
  attachment_rejected: FileX,
  declaration_received_again: Files,
  supplier_message_answered: Reply,
  supplier_message_answer_failed: MailX,
  supplier_message_done: CheckCheck,
  escalated: AlertTriangle,
  closed: CheckCircle2,
  committed: FileCheck,
  bounced: MailX,
  complained: ShieldAlert,
  auto_reminders_disabled: BellOff,
  auto_reminders_enabled: BellRing,
  note_added: StickyNote,
  note_edited: PenLine,
  note_removed: Eraser,
  note_attachment_added: Paperclip,
  note_attachment_removed: Paperclip,
  supplier_warning_acknowledged: ShieldAlert,
  recipient_overlap_acknowledged: Users,
}

const TONE_MAP: Record<InquiryTimelineTone, TimelineTone> = {
  ok: "success",
  warn: "warning",
  neutral: "muted",
}

// Localize the known event titles on the client (the backend title is English).
// Unknown/new event types fall back to the backend-provided title, so a new emitter
// never renders blank. Subtitles are localized from `detailKey` + `detailParams`
// (see timelineDetail.ts), falling back to the backend's English note.
const EVENT_TITLE_KEY: Record<string, TranslationKeys | undefined> = {
  drafted: "MC_INQ_TL_DRAFTED",
  sent: "MC_INQ_TL_SENT",
  inquiry_test_sent: "MC_INQ_TL_TEST_SENT",
  reminded: "MC_INQ_TL_REMINDED",
  reply_received: "MC_INQ_TL_REPLY_RECEIVED",
  declaration_linked: "MC_INQ_TL_DECLARATION_LINKED",
  additional_declaration: "MC_INQ_TL_ADDITIONAL_DECLARATION",
  attachment_rejected: "MC_INQ_TL_ATTACHMENT_REJECTED",
  declaration_received_again: "MC_INQ_TL_RECEIVED_AGAIN",
  supplier_message_answered: "MC_INQ_TL_MESSAGE_ANSWERED",
  supplier_message_answer_failed: "MC_INQ_TL_MESSAGE_ANSWER_FAILED",
  supplier_message_done: "MC_INQ_TL_MESSAGE_DONE",
  escalated: "MC_INQ_TL_ESCALATED",
  closed: "MC_INQ_TL_CLOSED",
  committed: "MC_INQ_TL_COMMITTED",
  partially_answered: "MC_INQ_TL_PARTIALLY_ANSWERED",
  regulations_waived: "MC_INQ_TL_REGULATIONS_WAIVED",
  bounced: "MC_INQ_TL_BOUNCED",
  complained: "MC_INQ_TL_COMPLAINED",
  auto_reminders_disabled: "MC_INQ_TL_AUTO_REMINDERS_OFF",
  auto_reminders_enabled: "MC_INQ_TL_AUTO_REMINDERS_ON",
  note_added: "MC_INQ_TL_NOTE_ADDED",
  note_edited: "MC_INQ_TL_NOTE_EDITED",
  note_removed: "MC_INQ_TL_NOTE_REMOVED",
  note_attachment_added: "MC_INQ_TL_NOTE_ATTACHMENT_ADDED",
  note_attachment_removed: "MC_INQ_TL_NOTE_ATTACHMENT_REMOVED",
  supplier_warning_acknowledged: "MC_INQ_TL_SUPPLIER_WARNING_ACKNOWLEDGED",
  recipient_overlap_acknowledged: "MC_INQ_TL_RECIPIENT_OVERLAP_ACKNOWLEDGED",
}

function toTimelineItem(
  event: InquiryTimelineEvent,
  index: number,
  title: string,
  subtitle: string | null,
  noteQuote: { lead: string | null; note: string } | null,
  viewEmail?: { label: string; onView: () => void },
  openLink?: { label: string; onOpen: () => void },
  accept?: { label: string; onAccept: () => void; pending: boolean },
  deletedLabel?: string,
  message?: ReactNode,
): TimelineItem {
  // Any step backed by an email (sent / reminder / reply) can open it.
  const showEmail = !!event.email && !!viewEmail
  const showLink = !!event.link && !!openLink
  const showAccept = !!accept
  const showDeleted = !!event.declarationDeleted && !!deletedLabel
  const hasText = !!subtitle || showLink || showAccept || showDeleted
  return {
    id: `${event.type}-${index}`,
    icon: EVENT_ICON[event.type] ?? History,
    tone: TONE_MAP[event.tone] ?? "muted",
    title,
    time: formatDate(event.timestamp) || undefined,
    detail:
      hasText || showEmail || message ? (
        <>
          {noteQuote ? (
            <>
              {noteQuote.lead && `${noteQuote.lead} `}
              <NoteText body={noteQuote.note} quoted />
            </>
          ) : (
            subtitle
          )}
          {showLink && openLink && (
            <>
              {subtitle ? " · " : ""}
              {/* Opens the linked declaration (the reply's resolving declaration). */}
              <button
                type="button"
                onClick={openLink.onOpen}
                className="inline-flex items-center gap-1 text-yellow1 hover:underline"
              >
                <FileText className="h-3 w-3" />
                {openLink.label}
              </button>
            </>
          )}
          {showAccept && accept && (
            <>
              {subtitle || showLink ? " · " : ""}
              {/* The reviewer's release of a held document: make it the inquiry's answer. */}
              <button
                type="button"
                onClick={accept.onAccept}
                disabled={accept.pending}
                className="inline-flex items-center gap-1 text-yellow1 hover:underline disabled:opacity-50"
              >
                <CheckCircle2 className="h-3 w-3" />
                {accept.label}
              </button>
            </>
          )}
          {showDeleted && (
            <>
              {subtitle ? " · " : ""}
              <span className="inline-flex items-center gap-1">
                <FileX className="h-3 w-3" />
                {deletedLabel}
              </span>
            </>
          )}
          {showEmail && viewEmail && (
            <>
              {hasText ? " · " : ""}
              <button
                type="button"
                onClick={viewEmail.onView}
                className="inline-flex items-center gap-1 text-yellow1 hover:underline"
              >
                <Mail className="h-3 w-3" />
                {viewEmail.label}
              </button>
            </>
          )}
          {message}
        </>
      ) : undefined,
  }
}

interface InquiryTimelineSectionProps {
  inquiry: SupplierInquiryDetail
}

/** Lifecycle history, with the email behind each step openable in a side sheet. */
export function InquiryTimelineSection({
  inquiry,
}: InquiryTimelineSectionProps) {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const [emailSheetOpen, setEmailSheetOpen] = useState(false)
  const [activeEmail, setActiveEmail] = useState<TimelineEmail | null>(null)
  const acceptMutation = useAcceptInquiryDeclaration()
  // A held document can be accepted while the inquiry is still open and that document
  // is not already the answer.
  const inquiryOpen =
    inquiry.status !== "answered" && inquiry.status !== "closed"

  if (!inquiry.timeline || inquiry.timeline.length === 0) return null

  const openEmail = (email: TimelineEmail) => {
    setActiveEmail(email)
    setEmailSheetOpen(true)
  }

  return (
    <div className="px-4 py-4">
      <SectionLabel className="mb-3 flex items-center gap-1.5">
        <History className="h-3.5 w-3.5" />
        {translate("MC_INQ_TIMELINE")}
      </SectionLabel>
      <Timeline
        items={inquiry.timeline.map((event, i) => {
          const email = event.email
          const link = event.link
          const titleKey = EVENT_TITLE_KEY[event.type]
          const title = titleKey ? translate(titleKey) : event.title
          return toTimelineItem(
            event,
            i,
            title,
            timelineDetailText(event, translate),
            timelineNoteQuote(event, translate),
            email
              ? {
                  label: translate("MC_INQ_VIEW_EMAIL"),
                  onView: () => openEmail(email),
                }
              : undefined,
            link
              ? {
                  label: translate("MC_INQ_OPEN_DECLARATION"),
                  // ref is the declaration id; open it in the review screen (the
                  // id-addressable declaration route).
                  onOpen: () =>
                    navigate(
                      `/material-compliance/supplier-studio/declarations/${link.ref}/review`,
                    ),
                }
              : undefined,
            event.type === "additional_declaration" &&
              link &&
              inquiryOpen &&
              link.ref !== inquiry.resolvedByDeclarationId
              ? {
                  label: translate("MC_INQ_ACCEPT_DECLARATION"),
                  onAccept: () =>
                    acceptMutation.mutate({
                      inquiryId: inquiry.id,
                      declarationId: link.ref,
                    }),
                  pending: acceptMutation.isPending,
                }
              : undefined,
            translate("MC_INQ_DECLARATION_DELETED"),
            event.supplierMessage ? (
              <SupplierMessageBlock
                inquiryId={inquiry.id}
                message={event.supplierMessage}
                sender={email?.sender ?? null}
              />
            ) : undefined,
          )
        })}
        subtle
      />

      <InquiryEmailSheet
        email={activeEmail}
        open={emailSheetOpen}
        onOpenChange={setEmailSheetOpen}
        recipientsTo={inquiry.recipientsTo}
        recipientsCc={inquiry.recipientsCc}
      />
    </div>
  )
}
